import { Maximize2, Mic, MicOff, PhoneOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { useVoiceCallStore } from 'state/store/chat/use-voice-call-store';
import { useVoiceCall } from '../hooks/use-voice-call';
import { getVoiceStatusLabelKey, VOICE_STATUS_LABEL_DEFAULTS } from '../types/voice-call.types';

/** Compact call strip shown above the composer while the overlay is minimized. */
export const VoiceCallDockedBar = () => {
  const { t } = useTranslation();
  const status = useVoiceCallStore((state) => state.status);
  const agentState = useVoiceCallStore((state) => state.agentState);
  const isMinimized = useVoiceCallStore((state) => state.isMinimized);
  const isMuted = useVoiceCallStore((state) => state.isMuted);
  const agentCaption = useVoiceCallStore((state) => state.agentCaption);
  const setMinimized = useVoiceCallStore((state) => state.setMinimized);
  const { hangUp, toggleMute } = useVoiceCall();

  if (status === 'idle' || !isMinimized) return null;

  const statusKey = getVoiceStatusLabelKey(status, agentState);
  const statusLabel = t(statusKey, { defaultValue: VOICE_STATUS_LABEL_DEFAULTS[statusKey] });
  const isBusy = status === 'in-call' && (agentState === 'thinking' || agentState === 'speaking');
  const muteLabel = isMuted
    ? t('VOICE_CALL_UNMUTE', { defaultValue: 'Unmute microphone' })
    : t('VOICE_CALL_MUTE', { defaultValue: 'Mute microphone' });
  const disabled = status === 'ending';

  return (
    <div
      data-testid="voice-call-docked-bar"
      className="mb-2 flex h-10 items-center gap-3 rounded-full bg-muted px-3 shadow-sm"
    >
      <span
        aria-hidden
        data-testid="voice-call-docked-dot"
        className={cn(
          'h-2.5 w-2.5 shrink-0 rounded-full bg-[var(--voice-orb-2)]',
          isBusy && 'animate-pulse'
        )}
      />
      <span className="shrink-0 text-sm font-medium" data-testid="voice-call-docked-status">
        {statusLabel}
      </span>
      {agentCaption && (
        <span className="hidden min-w-0 flex-1 truncate text-sm text-muted-foreground sm:inline">
          {agentCaption.text}
        </span>
      )}
      <div className="ml-auto flex shrink-0 items-center gap-1">
        <button
          type="button"
          aria-label={muteLabel}
          aria-pressed={isMuted}
          disabled={disabled}
          className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-background disabled:opacity-40"
          onClick={() => void toggleMute()}
        >
          {isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </button>
        <button
          type="button"
          aria-label={t('VOICE_CALL_SHOW_CALL', { defaultValue: 'Back to call' })}
          disabled={disabled}
          className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-background disabled:opacity-40"
          onClick={() => setMinimized(false)}
        >
          <Maximize2 className="h-4 w-4" />
        </button>
        <button
          type="button"
          aria-label={t('VOICE_CALL_END', { defaultValue: 'End call' })}
          disabled={disabled}
          className="flex h-8 w-8 items-center justify-center rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-40"
          onClick={() => void hangUp()}
        >
          <PhoneOff className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
